import renderSelect from 'components/FormControls/select'
import { Badge, Grid, Spinner, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Field } from 'redux-form'
import { formatToString } from 'utils/formatMoney'
import styles from 'modules/Forms/Billing/Billing-forms.module.scss'

export const Wallets = ({
  wallets = null,
  walletsLoading = false,
  loading = false,
  t = () => {},
  className = '',
  label = 'wallet',
  showEmptyOption = undefined,
  emptyOptionLabel = null,
  ...rest
}) => {
  if (!wallets) {
    return null
  }

  const walletsOptions = useMemo(() => {
    if (wallets?.length === 0) {
      return []
    }

    return wallets.map(
      ({
        currency,
        balance,
        id = undefined,
        name,
        financial_profile: { financial_profile_type: { type = null } = {}, custom_name },
      }) => ({
        label: custom_name ?? t(`finance::types.${type}`),
        balance,
        currency,
        name,
        custom_name,
        value: id === undefined ? currency : id === null ? 'null' : id,
      }),
    )
  }, [wallets, t])

  return (
    <div className={className}>
      <Typography
        variant={'Subtitle-30'}
        component={'h1'}
        className={'margin-bottom-16'}
      >
        {t('billing::form.wallet_id.title')}
      </Typography>
      <Field
        noResultsLabel={t('form.wallet_id.options.no_results')}
        name="wallet_id"
        options={walletsOptions}
        fullWidth
        className={styles['billing__select']}
        showEmptyOption={
          showEmptyOption !== undefined ? showEmptyOption : !loading && walletsOptions.length !== 0
        }
        readOnly={loading || walletsOptions.length === 0}
        label={t(label)}
        t={t}
        emptyOptionLabel={emptyOptionLabel || t('form.wallet_id.options.empty')}
        clearOptionLabel={t('common::select_clear_option')}
        component={renderSelect}
        activeOptionLabelTemplate={({ balance, currency, name }) =>
          `${name || t('wallets::nav.my_account')} — ${formatToString(balance, currency)}`
        }
        optionLabelTemplate={(wallet) => {
          const { balance, currency, label, name, custom_name } = wallet
          return (
            <Grid
              direction={'row'}
              flexAlign={'flex-start'}
              wrap={false}
              justifyContent={'space-between'}
            >
              <Grid
                direction={'column'}
                gap={4}
                flexGrow={1}
              >
                <Typography variant={'Body-15'}>{name || t('wallets::nav.my_account')}</Typography>
                <Grid
                  gap={8}
                  flexAlign={'center'}
                >
                  {currency && (
                    <Badge
                      rounded
                      bgColor={'Background-Six'}
                      color={'Label-GU-Primary'}
                    >
                      {currency}
                    </Badge>
                  )}

                  <Badge
                    rounded
                    bgColor={'Background-Six'}
                  >
                    {label}
                  </Badge>
                  {custom_name && (
                    <Typography
                      variant={'Body-15'}
                      color={'Label-Secondary'}
                    >
                      {custom_name}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid
                direction={'column'}
                alignItems={'flex-end'}
              >
                <Typography
                  variant={'Body-20'}
                  component={'span'}
                >
                  {formatToString(balance, currency)}
                </Typography>
              </Grid>
            </Grid>
          )
        }}
        ControlProps={{
          startAdornment: walletsLoading ? (
            <Spinner
              width={16}
              color={'Label-GU-Primary'}
            />
          ) : null,
        }}
        {...rest}
      />
    </div>
  )
}

Wallets.propTypes = {
  className: PropTypes.string,
  wallets: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  loading: PropTypes.bool,
  t: PropTypes.func,
  walletsLoading: PropTypes.bool,
  label: PropTypes.string,
  showEmptyOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  emptyOptionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
}
